/* eslint-disable @typescript-eslint/no-unused-vars */
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AJSTasksService } from 'src/admin-front/ajs-upgraded-providers';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { EngagementUserIdTypeEnum } from 'src/app/shared/components/choose-template-flow/shared/engagement-user-id-type.enum';
import { SingleMessage } from 'src/app/shared/components/single-message-editor/single-message';
import { EditorConfig } from 'src/app/shared/editor-config';
import { SiteTypeEnum } from 'src/app/shared/enums';
import { MessageSender } from 'src/app/shared/models/misc.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { ScrollService } from 'src/app/shared/services/scroll.service';
import { TaskProcessContext } from 'src/app/task-process/shared/task-process-context.class';
import { TaskProcessService } from 'src/app/task-process/shared/task-process.service';
import { environment } from 'src/environments/environment';

export abstract class EngagementAbstractService {
  /**
   * Contains the recipient (from), the sender (site).
   */
  taskProcessContext: TaskProcessContext;
  userProfileBS = new BehaviorSubject<boolean>(null);
  initContextBS = new BehaviorSubject<{ siteId: number; userExternalId: string }>(null);
  companyId: number = this.alcmeonContextService.companyId;
  externalUser: string;
  userExternalId: string;
  channelType: SiteTypeEnum;
  showComposerBS = new BehaviorSubject<boolean>(true);
  isCRMMode: boolean;
  optionsFromCRM!: string;
  message!: SingleMessage;
  userIdType?: EngagementUserIdTypeEnum;

  constructor(
    private sitesService: SitesService,
    private scrollService: ScrollService,
    private taskProcessService: TaskProcessService,
    private ajsTasksService: AJSTasksService,
    private basicService: BasicService,
    private alcmeonContextService: AlcmeonContextService,
    private route: ActivatedRoute
  ) {}

  abstract tryToFindRecipient(siteId: number, searchUserExternalType: string): Observable<MessageSender>;

  initTaskProcessContext(isCRMMode?: boolean) {
    if (!this.taskProcessContext) {
      this.taskProcessContext = new TaskProcessContext(
        this.basicService,
        this.scrollService,
        this.alcmeonContextService,
        this.taskProcessService,
        this.ajsTasksService
      );
    }
    this.taskProcessContext.engagement = true;
    this.taskProcessContext.isCRMMode = isCRMMode;
    if (this.channelType === SiteTypeEnum.Kakaotalk) {
      // Dummy processed task
      this.taskProcessContext.processedTaskOrActionId = '12345:task';
      const editorConfig = new EditorConfig(SiteTypeEnum.Kakaotalk, { isPrivate: true, isEngagement: true });
      this.taskProcessContext.editorConfig = editorConfig;
      this.message = {
        attachments: {
          buttons$: EMPTY,
        },
        links: [],
        editorConfig,
      } as any;
    }
  }

  initContext(siteId: number, userExternalIds: string) {
    this.clearContext();
    this.taskProcessContext.site = this.sitesService.enrichSiteWithLogo(this.sitesService.sitesV2Map.get(siteId));
    if (!userExternalIds) {
      return;
    }
    let searchUserExternalType = userExternalIds;
    if (this.channelType === SiteTypeEnum.Kakaotalk) {
      const [phoneNumber, appUserId, userKey] = userExternalIds.split(',');
      this.userIdType =
        (phoneNumber && EngagementUserIdTypeEnum.PHONE_NUMBER) ||
        (appUserId && EngagementUserIdTypeEnum.APP_USER_ID) ||
        (userKey && EngagementUserIdTypeEnum.USER_KEY);
      searchUserExternalType = phoneNumber || appUserId || userKey;
    }
    this.tryToFindRecipient(siteId, searchUserExternalType)
      .pipe(catchError(() => of({ name: null, external_id: null, id: null })))
      .subscribe({
        next: ({ name, external_id, id }) => {
          const searchUserExternalIdentifier = name;
          this.userExternalId = searchUserExternalType;
          if (searchUserExternalIdentifier) {
            const username = !id
              ? this.basicService.translate('engagement/send-message/choose-sender-recipient/unknown-user')
              : searchUserExternalIdentifier;
            this.externalUser = `${username} ${searchUserExternalType}`;
          } else {
            this.externalUser = `${searchUserExternalType}`;
          }
          this.userProfileBS.next(false);
          this.initContextBS.next({ userExternalId: external_id || searchUserExternalType, siteId });
        },
      });
  }

  clearContext() {
    this.externalUser = null;
    this.userProfileBS.next(false);
    this.initContextBS.next(null);
    if (this.taskProcessContext) {
      this.taskProcessContext.from = null;
      this.taskProcessContext.destroy();
    }
    if (!environment.bundledWithAngularJSApp && this.route) {
      // CRM Mode (ActivatedRoute works with Angular application => widget SF)
      const { queryParams } = this.route.snapshot;
      if (queryParams) {
        const { externalUser, from } = queryParams;
        // Hack due to latency while trying to unsubscribe the engagement page subscriptions.
        setTimeout(() => {
          if (externalUser) {
            this.externalUser = externalUser;
          }
          if (from) {
            try {
              this.taskProcessContext.from = JSON.parse(atob(decodeURIComponent(from)));
            } catch {
              // Nothing to do.
            }
          }
        });
      }
    }

    this.initTaskProcessContext(this.isCRMMode);
  }
}
